import { getRuntimeConfig } from "@/app/config/runtime-config";
import type { components } from "@/types/generated/invitation-api";

/**
 * Thin client over the CLMS-137 public first-credential contract (T023).
 * Types derive from src/types/generated/invitation-api.ts (generated via
 * `pnpm run spec:types:invitation` from the checked-in OpenAPI). Served by
 * clm-web-service-auth on the standard realm. The registration proof and the
 * fc1 authorization travel only in JSON request bodies — never in a path,
 * query string, or header — and the password only in the complete body.
 */

type Schemas = components["schemas"];

export type FirstCredentialStatusAction = Schemas["FirstCredentialStatusAction"];
export type FirstCredentialStatusRequest = Schemas["FirstCredentialStatusRequest"];
export type FirstCredentialStatusResponse = Schemas["FirstCredentialStatusResponse"];
export type FirstCredentialRedeemRequest = Schemas["FirstCredentialRedeemRequest"];
export type FirstCredentialValidateRequest = Schemas["FirstCredentialValidateRequest"];
export type FirstCredentialCompleteRequest = Schemas["FirstCredentialCompleteRequest"];
export type FirstCredentialRedeemed = Schemas["FirstCredentialRedeemed"];
export type FirstCredentialReadyOutcome = Schemas["FirstCredentialReadyOutcome"];
export type FirstCredentialCompleted = Schemas["FirstCredentialCompleted"];
export type FirstCredentialValidationFailed = Schemas["FirstCredentialValidationFailed"];
export type PasswordPolicyDescriptor = Schemas["PasswordPolicyDescriptor"];

/**
 * Rate limited with the parsed Retry-After hint (seconds) when the 429
 * carried one. The header is OPTIONAL in the OpenAPI response schema
 * (integer, minimum 1, no maximum): absent simply omits the hold (immediate
 * explicit retry allowed). Mirrors the parent registration client: the
 * header value drives the bounded user-activated retry hold and is never
 * displayed as a countdown.
 */
export type FirstCredentialRateLimited = Schemas["FirstCredentialRateLimited"] & {
  retryAfterSeconds?: number;
};

/**
 * Dependency unavailable with the parsed Retry-After hint (seconds) when the
 * 503 carried one. Same optional-header semantics as the 429: an absent
 * header simply omits the hold (immediate explicit retry allowed).
 */
export type FirstCredentialDependencyUnavailable =
  Schemas["FirstCredentialDependencyUnavailable"] & {
    retryAfterSeconds?: number;
  };

export type FirstCredentialSafeOutcome =
  | Schemas["FirstCredentialUnavailableOutcome"]
  | Schemas["FirstCredentialAlreadyEstablishedOutcome"]
  | Schemas["FirstCredentialExpiredOutcome"]
  | Schemas["FirstCredentialRevokedOutcome"]
  | Schemas["FirstCredentialSupportRequiredOutcome"]
  | Schemas["FirstCredentialInProgressOutcome"]
  | Schemas["FirstCredentialIdempotencyConflict"]
  | Schemas["FirstCredentialInvalidRequest"]
  | FirstCredentialRateLimited
  | FirstCredentialDependencyUnavailable;

export type FirstCredentialRedeemResult = FirstCredentialRedeemed | FirstCredentialSafeOutcome;

export type FirstCredentialValidateResult =
  | FirstCredentialReadyOutcome
  | FirstCredentialSafeOutcome;

export type FirstCredentialCompleteResult =
  | FirstCredentialCompleted
  | FirstCredentialValidationFailed
  | FirstCredentialSafeOutcome;

export class FirstCredentialApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "FirstCredentialApiError";
    this.status = status;
  }
}

/** Closed contract action set for the status operation. */
const FIRST_CREDENTIAL_STATUS_ACTIONS: readonly FirstCredentialStatusAction[] = [
  "set_password_available",
  "sign_in",
  "no_action",
];

export function isFirstCredentialStatusAction(
  value: unknown,
): value is FirstCredentialStatusAction {
  return (
    typeof value === "string" &&
    (FIRST_CREDENTIAL_STATUS_ACTIONS as readonly string[]).includes(value)
  );
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Contract shape (additionalProperties: false): exactly `action` +
 * `correlationId`, a closed-set action, and a UUID correlation reference.
 */
function isFirstCredentialStatusResponse(body: unknown): body is FirstCredentialStatusResponse {
  if (!isPlainObject(body)) {
    return false;
  }
  const keys = Object.keys(body);
  if (keys.length !== 2 || !keys.includes("action") || !keys.includes("correlationId")) {
    return false;
  }
  return isFirstCredentialStatusAction(body["action"]) && isUuid(body["correlationId"]);
}

/**
 * Retry-After hold bounds (seconds). The header is OPTIONAL on both 429 and
 * 503 (schema: integer, minimum 1, NO maximum): when present it must be an
 * integer >= 1 to be contract-valid; contract-valid values past an hour are
 * honored but the UI hold is CLAMPED to the max rather than scheduling an
 * absurd wait. Exported as the canonical bounds so UI holds (e.g. the
 * registration follow-up's redeem retry) share one definition.
 */
export const RETRY_AFTER_MIN_SECONDS = 1;
export const RETRY_AFTER_MAX_SECONDS = 3600;

type RetryAfterHint =
  | { readonly kind: "absent" }
  | { readonly kind: "malformed" }
  | { readonly kind: "usable"; readonly seconds: number };

/**
 * Shared Retry-After admission for the 429 and 503 branches (the header is
 * optional on both): absent → no hold; present but non-integer or < 1 →
 * malformed (the envelope fails closed); present and >= 1 → usable, clamped
 * to RETRY_AFTER_MAX_SECONDS so a contract-legal but oversized window never
 * schedules an unbounded UI hold.
 */
function parseRetryAfterHint(header: string | null): RetryAfterHint {
  if (header === null) {
    return { kind: "absent" };
  }
  // Deliberate whitespace handling (unchanged): surrounding whitespace is
  // trimmed, then the remainder must be digits only — signs, decimals,
  // units, and interior whitespace stay malformed.
  const trimmed = header.trim();
  if (!/^\d+$/.test(trimmed)) {
    return { kind: "malformed" };
  }
  // Leading zeros carry no value: normalize them away BEFORE any
  // length-based shortcut, so "00000000001" is the value 1 and an all-zero
  // string of any length is the value 0 (below the schema minimum —
  // malformed).
  const normalized = trimmed.replace(/^0+(?=\d)/, "");
  if (normalized === "0") {
    return { kind: "malformed" };
  }
  // The schema sets NO maximum, so digit strings of any length are
  // contract-legal. Past 10 SIGNIFICANT digits parseInt precision is not
  // trusted: any value that long is far beyond the clamp, so it
  // short-circuits straight to the max hold.
  if (normalized.length > 10) {
    return { kind: "usable", seconds: RETRY_AFTER_MAX_SECONDS };
  }
  const seconds = Number.parseInt(normalized, 10);
  if (seconds < RETRY_AFTER_MIN_SECONDS) {
    return { kind: "malformed" };
  }
  return { kind: "usable", seconds: Math.min(seconds, RETRY_AFTER_MAX_SECONDS) };
}

/** fc1 shape from the OpenAPI FirstCredentialAuthorization schema. */
const FC1_AUTHORIZATION_PATTERN = /^fc1\.[0-9a-fA-F-]{36}\.[A-Za-z0-9_-]{43}$/;

function isFc1Authorization(value: unknown): value is string {
  return typeof value === "string" && FC1_AUTHORIZATION_PATTERN.test(value);
}

/** RFC 3339 date-time (OpenAPI `format: date-time`) with capture groups. */
const RFC3339_DATE_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[Tt](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:[Zz]|[+-](\d{2}):(\d{2}))$/;

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    return isLeapYear(year) ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/**
 * Real calendar validation, not Date.parse: the JS date parser silently
 * rolls impossible calendar dates over (e.g. a non-leap Feb 29 becomes
 * Mar 1), so each component is range-checked explicitly, including
 * days-in-month with leap years. second 60 is allowed — RFC 3339 permits
 * leap seconds in `date-time`.
 */
function isRfc3339DateTime(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }
  const match = RFC3339_DATE_TIME_PATTERN.exec(value);
  if (!match) {
    return false;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[7] === undefined ? 0 : Number(match[7]);
  const offsetMinute = match[8] === undefined ? 0 : Number(match[8]);
  return (
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth(year, month) &&
    hour <= 23 &&
    minute <= 59 &&
    second <= 60 &&
    offsetHour <= 23 &&
    offsetMinute <= 59
  );
}

/**
 * PasswordPolicyDescriptor: integer minimumLength within the contract's
 * [8, 128] bounds plus all four requirement booleans. The optional
 * `disallowLeadingTrailingWhitespace` flag may be absent (pre-flag
 * descriptors) but, when present as an OWN property, must be a boolean (an
 * inherited key is not part of the server-sent descriptor). The schema allows
 * unknown extra fields (additionalProperties: true), so no key check here.
 * Shared with the registration-invitation client (FR-050 member lookup).
 */
export function isPasswordPolicyDescriptor(value: unknown): value is PasswordPolicyDescriptor {
  if (!isPlainObject(value)) {
    return false;
  }
  const minimumLength = value["minimumLength"];
  return (
    typeof minimumLength === "number" &&
    Number.isInteger(minimumLength) &&
    minimumLength >= 8 &&
    minimumLength <= 128 &&
    typeof value["requireUppercase"] === "boolean" &&
    typeof value["requireLowercase"] === "boolean" &&
    typeof value["requireNumber"] === "boolean" &&
    typeof value["requireSymbol"] === "boolean" &&
    (!Object.hasOwn(value, "disallowLeadingTrailingWhitespace") ||
      typeof value["disallowLeadingTrailingWhitespace"] === "boolean")
  );
}

/**
 * Whether the descriptor enforces the leading/trailing-whitespace rule. Reads
 * the flag with the same own-property semantics as `isPasswordPolicyDescriptor`
 * so page reads can never disagree with the guard (or the server).
 */
export function policyDisallowsEdgeWhitespace(policy: PasswordPolicyDescriptor): boolean {
  return (
    Object.hasOwn(policy, "disallowLeadingTrailingWhitespace") &&
    policy.disallowLeadingTrailingWhitespace === true
  );
}

/** Exact-key check for the contract's additionalProperties: false envelopes. */
function hasExactKeys(body: Record<string, unknown>, expected: readonly string[]): boolean {
  const keys = Object.keys(body);
  return keys.length === expected.length && expected.every((key) => keys.includes(key));
}

const REDEEMED_KEYS = [
  "outcome",
  "authorization",
  "expiresAt",
  "passwordPolicy",
  "correlationId",
] as const;
const READY_KEYS = ["outcome", "expiresAt", "passwordPolicy", "correlationId"] as const;
const COMPLETED_KEYS = ["outcome", "nextAction", "completedAt", "correlationId"] as const;
const VALIDATION_FAILED_KEYS = [
  "outcome",
  "code",
  "message",
  "fieldErrors",
  "correlationId",
] as const;
const FIELD_ERROR_KEYS = ["field", "code", "message"] as const;
const OUTCOME_ENVELOPE_KEYS = ["outcome", "code", "message", "correlationId"] as const;

// Contract const `message`/`code` values, typed against the generated
// literal types so a contract change fails compilation here (V20).
const RATE_LIMITED_MESSAGE: Schemas["FirstCredentialRateLimited"]["message"] =
  "Please wait before trying first-credential setup again.";
const RATE_LIMITED_CODE: Schemas["FirstCredentialRateLimited"]["code"] = "RATE_LIMITED";
const DEPENDENCY_UNAVAILABLE_MESSAGE: Schemas["FirstCredentialDependencyUnavailable"]["message"] =
  "First-credential setup is temporarily unavailable. Try again later.";
const DEPENDENCY_UNAVAILABLE_CODE: Schemas["FirstCredentialDependencyUnavailable"]["code"] =
  "DEPENDENCY_UNAVAILABLE";
const ALREADY_ESTABLISHED_MESSAGE: Schemas["FirstCredentialAlreadyEstablishedOutcome"]["message"] =
  "Your password is already set. Sign in or use password recovery.";
const ALREADY_ESTABLISHED_CODE: Schemas["FirstCredentialAlreadyEstablishedOutcome"]["code"] =
  "FIRST_CREDENTIAL_ALREADY_ESTABLISHED";
const SUPPORT_REQUIRED_MESSAGE: Schemas["FirstCredentialSupportRequiredOutcome"]["message"] =
  "Additional support is required to finish first-credential setup.";
const SUPPORT_REQUIRED_CODE: Schemas["FirstCredentialSupportRequiredOutcome"]["code"] =
  "FIRST_CREDENTIAL_SUPPORT_REQUIRED";
const UNAVAILABLE_MESSAGE: Schemas["FirstCredentialUnavailableOutcome"]["message"] =
  "This first-credential request cannot be completed.";
const UNAVAILABLE_CODE: Schemas["FirstCredentialUnavailableOutcome"]["code"] =
  "FIRST_CREDENTIAL_UNAVAILABLE";
const INVALID_REQUEST_MESSAGE: Schemas["FirstCredentialInvalidRequest"]["message"] =
  "The first-credential request could not be processed.";
const INVALID_REQUEST_CODE: Schemas["FirstCredentialInvalidRequest"]["code"] = "INVALID_REQUEST";
const EXPIRED_MESSAGE: Schemas["FirstCredentialExpiredOutcome"]["message"] =
  "This first-credential session has expired. Restart from your registration link or contact support.";
const EXPIRED_CODE: Schemas["FirstCredentialExpiredOutcome"]["code"] = "FIRST_CREDENTIAL_EXPIRED";
const REVOKED_MESSAGE: Schemas["FirstCredentialRevokedOutcome"]["message"] =
  "This first-credential session is no longer available. Restart from your registration link or contact support.";
const REVOKED_CODE: Schemas["FirstCredentialRevokedOutcome"]["code"] = "FIRST_CREDENTIAL_REVOKED";
const IN_PROGRESS_MESSAGE: Schemas["FirstCredentialInProgressOutcome"]["message"] =
  "First-credential setup is already in progress.";
const IN_PROGRESS_CODE: Schemas["FirstCredentialInProgressOutcome"]["code"] =
  "FIRST_CREDENTIAL_IN_PROGRESS";
const IDEMPOTENCY_CONFLICT_MESSAGE: Schemas["FirstCredentialIdempotencyConflict"]["message"] =
  "This request cannot be retried with a different first-credential authorization.";
const IDEMPOTENCY_CONFLICT_CODE: Schemas["FirstCredentialIdempotencyConflict"]["code"] =
  "IDEMPOTENCY_CONFLICT";
const VALIDATION_FAILED_MESSAGE: Schemas["FirstCredentialValidationFailed"]["message"] =
  "Password requirements were not met.";
const VALIDATION_FAILED_CODE: Schemas["FirstCredentialValidationFailed"]["code"] =
  "PASSWORD_REQUIREMENTS_NOT_MET";
const COMPLETED_NEXT_ACTION: Schemas["FirstCredentialCompleted"]["nextAction"] = "sign_in";

type FirstCredentialFieldError = Schemas["FirstCredentialValidationFailed"]["fieldErrors"][number];
const FIELD_ERROR_FIELD: FirstCredentialFieldError["field"] = "password";
const FIELD_ERROR_CODE: FirstCredentialFieldError["code"] = "password_requirements_not_met";
/** OpenAPI fieldErrors item message maxLength. */
const FIELD_ERROR_MESSAGE_MAX_LENGTH = 300;

interface TerminalEnvelope {
  code: string;
  message: string;
}

/**
 * Terminal redeem denials the contract serves, as exact (status →
 * outcome → const code + const message) tuples: 200 serves
 * already_established, support_required, and
 * unavailable/FIRST_CREDENTIAL_UNAVAILABLE; 400 serves only
 * unavailable/INVALID_REQUEST.
 */
const REDEEM_TERMINAL_ENVELOPES_BY_STATUS: ReadonlyMap<
  number,
  ReadonlyMap<string, TerminalEnvelope>
> = new Map([
  [
    200,
    new Map<string, TerminalEnvelope>([
      [
        "already_established",
        { code: ALREADY_ESTABLISHED_CODE, message: ALREADY_ESTABLISHED_MESSAGE },
      ],
      ["support_required", { code: SUPPORT_REQUIRED_CODE, message: SUPPORT_REQUIRED_MESSAGE }],
      ["unavailable", { code: UNAVAILABLE_CODE, message: UNAVAILABLE_MESSAGE }],
    ]),
  ],
  [
    400,
    new Map([["unavailable", { code: INVALID_REQUEST_CODE, message: INVALID_REQUEST_MESSAGE }]]),
  ],
]);

/**
 * Terminal validate denials (operation validateFirstCredentialAuthorization):
 * 200 serves expired, already_established, revoked, support_required, and
 * unavailable/FIRST_CREDENTIAL_UNAVAILABLE alongside `ready`; 400 serves
 * only unavailable/INVALID_REQUEST. Validate has no 409/422 responses.
 */
const VALIDATE_TERMINAL_ENVELOPES_BY_STATUS: ReadonlyMap<
  number,
  ReadonlyMap<string, TerminalEnvelope>
> = new Map([
  [
    200,
    new Map<string, TerminalEnvelope>([
      ["expired", { code: EXPIRED_CODE, message: EXPIRED_MESSAGE }],
      [
        "already_established",
        { code: ALREADY_ESTABLISHED_CODE, message: ALREADY_ESTABLISHED_MESSAGE },
      ],
      ["revoked", { code: REVOKED_CODE, message: REVOKED_MESSAGE }],
      ["support_required", { code: SUPPORT_REQUIRED_CODE, message: SUPPORT_REQUIRED_MESSAGE }],
      ["unavailable", { code: UNAVAILABLE_CODE, message: UNAVAILABLE_MESSAGE }],
    ]),
  ],
  [
    400,
    new Map([["unavailable", { code: INVALID_REQUEST_CODE, message: INVALID_REQUEST_MESSAGE }]]),
  ],
]);

/**
 * Terminal complete denials (operation completeFirstCredentialAuthorization):
 * 200 serves already_established, expired, revoked, and
 * unavailable/FIRST_CREDENTIAL_UNAVAILABLE alongside `completed`; 400 serves
 * only unavailable/INVALID_REQUEST; 409 serves in_progress,
 * support_required (409 ONLY for complete — unlike redeem/validate), and the
 * idempotency conflict (outcome unavailable + code IDEMPOTENCY_CONFLICT).
 * validation_failed (422) is handled separately — it carries fieldErrors.
 */
const COMPLETE_TERMINAL_ENVELOPES_BY_STATUS: ReadonlyMap<
  number,
  ReadonlyMap<string, TerminalEnvelope>
> = new Map([
  [
    200,
    new Map<string, TerminalEnvelope>([
      [
        "already_established",
        { code: ALREADY_ESTABLISHED_CODE, message: ALREADY_ESTABLISHED_MESSAGE },
      ],
      ["expired", { code: EXPIRED_CODE, message: EXPIRED_MESSAGE }],
      ["revoked", { code: REVOKED_CODE, message: REVOKED_MESSAGE }],
      ["unavailable", { code: UNAVAILABLE_CODE, message: UNAVAILABLE_MESSAGE }],
    ]),
  ],
  [
    400,
    new Map([["unavailable", { code: INVALID_REQUEST_CODE, message: INVALID_REQUEST_MESSAGE }]]),
  ],
  [
    409,
    new Map<string, TerminalEnvelope>([
      ["in_progress", { code: IN_PROGRESS_CODE, message: IN_PROGRESS_MESSAGE }],
      ["support_required", { code: SUPPORT_REQUIRED_CODE, message: SUPPORT_REQUIRED_MESSAGE }],
      ["unavailable", { code: IDEMPOTENCY_CONFLICT_CODE, message: IDEMPOTENCY_CONFLICT_MESSAGE }],
    ]),
  ],
]);

/**
 * Shared fail-closed admission for the safe outcomes every first-credential
 * operation serves: 429 rate_limited with code RATE_LIMITED and its const
 * message; 503 dependency_unavailable with its const code/message; both
 * with an OPTIONAL Retry-After (present-and-valid attaches the hold —
 * clamped to RETRY_AFTER_MAX_SECONDS — present-and-invalid is malformed,
 * absent allows immediate explicit retry); and the operation's per-status
 * terminal (outcome, code, message) tuples — every terminal `message` is
 * pinned to its schema const, and extra outer properties are rejected
 * (additionalProperties: false). Returns null on ANY mismatch so the
 * operation validator throws its own deliberately generic error.
 */
function admitSafeOutcome(
  status: number,
  body: Record<string, unknown>,
  retryAfterHeader: string | null,
  terminalEnvelopesByStatus: ReadonlyMap<number, ReadonlyMap<string, TerminalEnvelope>>,
): FirstCredentialSafeOutcome | null {
  const { outcome, code, message, correlationId } = body;
  if (outcome === "rate_limited") {
    // Retry-After is optional in the schema: absent → no hold (immediate
    // explicit retry); present-and-invalid → malformed.
    const retryAfter = parseRetryAfterHint(retryAfterHeader);
    if (
      status === 429 &&
      hasExactKeys(body, OUTCOME_ENVELOPE_KEYS) &&
      code === RATE_LIMITED_CODE &&
      message === RATE_LIMITED_MESSAGE &&
      isUuid(correlationId) &&
      retryAfter.kind !== "malformed"
    ) {
      const limited = body as unknown as Schemas["FirstCredentialRateLimited"];
      return retryAfter.kind === "absent"
        ? limited
        : { ...limited, retryAfterSeconds: retryAfter.seconds };
    }
  } else if (outcome === "dependency_unavailable") {
    // Same optional-header semantics as the 429 branch above.
    const retryAfter = parseRetryAfterHint(retryAfterHeader);
    if (
      status === 503 &&
      hasExactKeys(body, OUTCOME_ENVELOPE_KEYS) &&
      code === DEPENDENCY_UNAVAILABLE_CODE &&
      message === DEPENDENCY_UNAVAILABLE_MESSAGE &&
      isUuid(correlationId) &&
      retryAfter.kind !== "malformed"
    ) {
      const outage = body as unknown as Schemas["FirstCredentialDependencyUnavailable"];
      return retryAfter.kind === "absent"
        ? outage
        : { ...outage, retryAfterSeconds: retryAfter.seconds };
    }
  } else if (typeof outcome === "string") {
    const envelope = terminalEnvelopesByStatus.get(status)?.get(outcome);
    if (
      envelope !== undefined &&
      hasExactKeys(body, OUTCOME_ENVELOPE_KEYS) &&
      code === envelope.code &&
      message === envelope.message &&
      isUuid(correlationId)
    ) {
      return body as unknown as FirstCredentialSafeOutcome;
    }
  }
  return null;
}

/**
 * Fail-closed redeem validation coupling the exact body shape to the HTTP
 * status the contract serves it from: 201 (and only 201) redeemed with the
 * exact envelope, an fc1-patterned authorization, a calendar-valid RFC 3339
 * expiresAt, and a full password policy; plus the shared safe outcomes and
 * the per-status terminal tuples above. A malformed or status-mismatched
 * response must never retain the registration proof or enable repeat
 * redemption, so it throws — the caller's uncertain-terminal path, which
 * scrubs the proof.
 */
function validateRedeemResult(
  status: number,
  body: unknown,
  retryAfterHeader: string | null,
): FirstCredentialRedeemResult {
  if (isPlainObject(body)) {
    if (body["outcome"] === "redeemed") {
      if (
        status === 201 &&
        hasExactKeys(body, REDEEMED_KEYS) &&
        isFc1Authorization(body["authorization"]) &&
        isRfc3339DateTime(body["expiresAt"]) &&
        isPasswordPolicyDescriptor(body["passwordPolicy"]) &&
        isUuid(body["correlationId"])
      ) {
        return body as unknown as FirstCredentialRedeemed;
      }
    } else {
      const safe = admitSafeOutcome(
        status,
        body,
        retryAfterHeader,
        REDEEM_TERMINAL_ENVELOPES_BY_STATUS,
      );
      if (safe !== null) {
        return safe;
      }
    }
  }
  // Deliberately generic: no bootstrap/authorization-existence detail.
  throw new FirstCredentialApiError(
    `First-credential redemption returned an unusable response (status ${String(status)}).`,
    status,
  );
}

/**
 * Fail-closed validate validation: 200 (and only 200) ready with the exact
 * envelope, a calendar-valid RFC 3339 expiresAt, and a full password policy;
 * plus the shared safe outcomes and validate's per-status terminal tuples.
 * A `completed`, `in_progress`, or any other out-of-matrix outcome — or a
 * ready body missing its password policy — must never drive the set-password
 * form, so it throws (the caller's generic unavailable path).
 */
function validateValidateResult(
  status: number,
  body: unknown,
  retryAfterHeader: string | null,
): FirstCredentialValidateResult {
  if (isPlainObject(body)) {
    if (body["outcome"] === "ready") {
      if (
        status === 200 &&
        hasExactKeys(body, READY_KEYS) &&
        isRfc3339DateTime(body["expiresAt"]) &&
        isPasswordPolicyDescriptor(body["passwordPolicy"]) &&
        isUuid(body["correlationId"])
      ) {
        return body as unknown as FirstCredentialReadyOutcome;
      }
    } else {
      const safe = admitSafeOutcome(
        status,
        body,
        retryAfterHeader,
        VALIDATE_TERMINAL_ENVELOPES_BY_STATUS,
      );
      if (safe !== null) {
        return safe;
      }
    }
  }
  // Deliberately generic: no bootstrap/authorization-existence detail.
  throw new FirstCredentialApiError(
    `First-credential validation returned an unusable response (status ${String(status)}).`,
    status,
  );
}

/**
 * Contract fieldErrors (422 validation_failed): exactly ONE entry
 * (minItems/maxItems: 1) with the exact {field, code, message} shape, const
 * field/code, and a bounded plain-string message (maxLength: 300 — counted
 * in Unicode code points per OpenAPI/JSON Schema, not UTF-16 code units,
 * hence Array.from rather than .length).
 */
function isValidationFailedFieldErrors(value: unknown): boolean {
  if (!Array.isArray(value) || value.length !== 1) {
    return false;
  }
  const entry: unknown = value[0];
  return (
    isPlainObject(entry) &&
    hasExactKeys(entry, FIELD_ERROR_KEYS) &&
    entry["field"] === FIELD_ERROR_FIELD &&
    entry["code"] === FIELD_ERROR_CODE &&
    typeof entry["message"] === "string" &&
    Array.from(entry["message"]).length <= FIELD_ERROR_MESSAGE_MAX_LENGTH
  );
}

/**
 * Fail-closed complete validation: 200 (and only 200) completed with the
 * exact envelope, the const nextAction, and a calendar-valid RFC 3339
 * completedAt; 422 (and only 422) validation_failed with its const
 * code/message and the exact single-entry fieldErrors shape; plus the shared
 * safe outcomes and complete's per-status terminal tuples (including the 409
 * idempotency conflict). A thrown error IS the caller's safe path: the
 * caller treats it as an uncertain outcome (first-write-wins retry with the
 * held idempotency key), so a malformed validation_failed throws here BEFORE
 * it could be handled as a recoverable typed result and skip that retention.
 */
function validateCompleteResult(
  status: number,
  body: unknown,
  retryAfterHeader: string | null,
): FirstCredentialCompleteResult {
  if (isPlainObject(body)) {
    const outcome = body["outcome"];
    if (outcome === "completed") {
      if (
        status === 200 &&
        hasExactKeys(body, COMPLETED_KEYS) &&
        body["nextAction"] === COMPLETED_NEXT_ACTION &&
        isRfc3339DateTime(body["completedAt"]) &&
        isUuid(body["correlationId"])
      ) {
        return body as unknown as FirstCredentialCompleted;
      }
    } else if (outcome === "validation_failed") {
      if (
        status === 422 &&
        hasExactKeys(body, VALIDATION_FAILED_KEYS) &&
        body["code"] === VALIDATION_FAILED_CODE &&
        body["message"] === VALIDATION_FAILED_MESSAGE &&
        isValidationFailedFieldErrors(body["fieldErrors"]) &&
        isUuid(body["correlationId"])
      ) {
        return body as unknown as FirstCredentialValidationFailed;
      }
    } else {
      const safe = admitSafeOutcome(
        status,
        body,
        retryAfterHeader,
        COMPLETE_TERMINAL_ENVELOPES_BY_STATUS,
      );
      if (safe !== null) {
        return safe;
      }
    }
  }
  // Deliberately generic: no bootstrap/authorization-existence detail.
  throw new FirstCredentialApiError(
    `First-credential completion returned an unusable response (status ${String(status)}).`,
    status,
  );
}

export interface FirstCredentialClient {
  /** Non-minting status resolution: the proof travels only in the body.
   * Callers racing the read against an absolute deadline (the FR-047
   * financial-origin settlement window) pass an AbortSignal. */
  status(
    request: FirstCredentialStatusRequest,
    options?: { signal?: AbortSignal },
  ): Promise<FirstCredentialStatusResponse>;
  /** Mints the single fc1 authorization; only ever run on user activation. */
  redeem(request: FirstCredentialRedeemRequest): Promise<FirstCredentialRedeemResult>;
  /** The page aborts an in-flight validate/complete at pagehide and on
   * unmount (#141): a frozen BFCache document must not keep a request
   * carrying the authorization (and password) pending on the network stack. */
  validate(
    request: FirstCredentialValidateRequest,
    options?: { signal?: AbortSignal },
  ): Promise<FirstCredentialValidateResult>;
  complete(
    request: FirstCredentialCompleteRequest,
    idempotencyKey: string,
    options?: { signal?: AbortSignal },
  ): Promise<FirstCredentialCompleteResult>;
}

export class HttpFirstCredentialClient implements FirstCredentialClient {
  private readonly fetcher: typeof fetch;

  constructor(fetcher: typeof fetch = globalThis.fetch) {
    // Bind so native fetch is not invoked with the client instance as `this`
    // (same "Illegal invocation" hazard handled in HttpAuthClient).
    this.fetcher = fetcher === globalThis.fetch ? fetcher.bind(globalThis) : fetcher;
  }

  async status(
    request: FirstCredentialStatusRequest,
    options?: { signal?: AbortSignal },
  ): Promise<FirstCredentialStatusResponse> {
    const { status, body } = await this.send("/public/first-credential-authorizations/status", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
      signal: options?.signal ?? null,
    });
    // Fail closed on the status AND the exact contract shape: an action
    // outside the closed set, an extra property, a non-UUID correlation
    // reference, or a non-200 status must never drive a UI decision — the
    // thrown rejection maps to the safe status_failed re-entry/support
    // guidance.
    if (status !== 200 || !isFirstCredentialStatusResponse(body)) {
      throw new FirstCredentialApiError(
        "First-credential status returned an unrecognized response.",
        status,
      );
    }
    return body;
  }

  async redeem(request: FirstCredentialRedeemRequest): Promise<FirstCredentialRedeemResult> {
    const { status, body, retryAfterHeader } = await this.send(
      "/public/first-credential-authorizations/redeem",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      },
    );
    return validateRedeemResult(status, body, retryAfterHeader);
  }

  async validate(
    request: FirstCredentialValidateRequest,
    options?: { signal?: AbortSignal },
  ): Promise<FirstCredentialValidateResult> {
    const { status, body, retryAfterHeader } = await this.send(
      "/public/first-credential-authorizations/validate",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
        signal: options?.signal ?? null,
      },
    );
    return validateValidateResult(status, body, retryAfterHeader);
  }

  async complete(
    request: FirstCredentialCompleteRequest,
    idempotencyKey: string,
    options?: { signal?: AbortSignal },
  ): Promise<FirstCredentialCompleteResult> {
    const { status, body, retryAfterHeader } = await this.send(
      "/public/first-credential-authorizations/complete",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify(request),
        signal: options?.signal ?? null,
      },
    );
    return validateCompleteResult(status, body, retryAfterHeader);
  }

  /** Single fetch + parse point: the per-operation validators own admission. */
  private async send(
    path: `/${string}`,
    init: RequestInit,
  ): Promise<{ status: number; body: unknown; retryAfterHeader: string | null }> {
    const response = await this.fetcher(this.url(path), {
      credentials: "include",
      ...init,
    });
    const body: unknown = await response.json().catch(() => undefined);
    return {
      status: response.status,
      body,
      retryAfterHeader: response.headers.get("Retry-After"),
    };
  }

  private url(path: `/${string}`): URL {
    // Resolved lazily per request so client construction is safe before the
    // runtime config is loaded. Public endpoints live on the standard realm.
    return new URL(path, getRuntimeConfig().authBaseUrl);
  }
}
